/**
 * Cart Service - Handles all cart operations using Shopify Storefront Cart API
 * Replaces the deprecated checkout API from shopify-buy SDK
 */

const STOREFRONT_API_URL = `https://${process.env.SHOP_NAME}.myshopify.com/api/2024-10/graphql.json`;
const STOREFRONT_ACCESS_TOKEN = process.env.SHOPIFY_ACCESS_TOKEN;

/**
 * Helper function to ensure variant ID is in the correct GID format
 */
function ensureGidFormat(variantId) {
  // If it's already in GID format, return as is
  if (variantId.startsWith('gid://shopify/ProductVariant/')) {
    return variantId;
  }
  
  // If it's a base64 encoded ID (from gatsby-source-shopify), decode it
  if (variantId.includes('==') || variantId.length > 20) {
    try {
      const decoded = atob(variantId);
      if (decoded.startsWith('gid://')) {
        return decoded;
      }
    } catch (e) {
      // Not a base64 string, continue
    }
  }
  
  // Otherwise, assume it's a numeric ID and format it
  const numericId = variantId.replace(/\D/g, '');
  return `gid://shopify/ProductVariant/${numericId}`;
}

/**
 * Helper function to make GraphQL requests to Storefront API
 */
async function storefrontFetch(query, variables = {}) {
  const response = await fetch(STOREFRONT_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  
  if (data.errors) {
    throw new Error(data.errors.map(e => e.message).join(', '));
  }

  return data.data;
}

/**
 * Create a new cart
 */
export async function createCart() {
  const query = `
    mutation cartCreate {
      cartCreate {
        cart {
          id
          createdAt
          updatedAt
          lines(first: 100) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    product {
                      id
                      title
                      handle
                      images(first: 1) {
                        edges {
                          node {
                            src
                            altText
                          }
                        }
                      }
                    }
                  }
                }
                attributes {
                  key
                  value
                }
              }
            }
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
            subtotalAmount {
              amount
              currencyCode
            }
          }
          checkoutUrl
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await storefrontFetch(query);
  
  if (data.cartCreate.userErrors.length > 0) {
    throw new Error(data.cartCreate.userErrors.map(e => e.message).join(', '));
  }

  return data.cartCreate.cart;
}

/**
 * Fetch an existing cart by ID
 */
export async function fetchCart(cartId) {
  const query = `
    query getCart($cartId: ID!) {
      cart(id: $cartId) {
        id
        createdAt
        updatedAt
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    id
                    title
                    handle
                    images(first: 1) {
                      edges {
                        node {
                          src
                          altText
                        }
                      }
                    }
                  }
                }
              }
              attributes {
                key
                value
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
        checkoutUrl
      }
    }
  `;

  const data = await storefrontFetch(query, { cartId });
  return data.cart;
}

/**
 * Add items to cart
 */
export async function addToCart(cartId, lines) {
  // Ensure all merchandise IDs are in the correct format
  const formattedLines = lines.map(line => ({
    ...line,
    merchandiseId: ensureGidFormat(line.merchandiseId),
  }));

  const query = `
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          id
          createdAt
          updatedAt
          lines(first: 100) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    product {
                      id
                      title
                      handle
                      images(first: 1) {
                        edges {
                          node {
                            src
                            altText
                          }
                        }
                      }
                    }
                  }
                }
                attributes {
                  key
                  value
                }
              }
            }
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
            subtotalAmount {
              amount
              currencyCode
            }
          }
          checkoutUrl
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await storefrontFetch(query, { cartId, lines: formattedLines });
  
  if (data.cartLinesAdd.userErrors.length > 0) {
    throw new Error(data.cartLinesAdd.userErrors.map(e => e.message).join(', '));
  }

  return data.cartLinesAdd.cart;
}

/**
 * Remove lines from cart
 */
export async function removeFromCart(cartId, lineIds) {
  const query = `
    mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart {
          id
          createdAt
          updatedAt
          lines(first: 100) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    product {
                      id
                      title
                      handle
                      images(first: 1) {
                        edges {
                          node {
                            src
                            altText
                          }
                        }
                      }
                    }
                  }
                }
                attributes {
                  key
                  value
                }
              }
            }
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
            subtotalAmount {
              amount
              currencyCode
            }
          }
          checkoutUrl
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await storefrontFetch(query, { cartId, lineIds });
  
  if (data.cartLinesRemove.userErrors.length > 0) {
    throw new Error(data.cartLinesRemove.userErrors.map(e => e.message).join(', '));
  }

  return data.cartLinesRemove.cart;
}

/**
 * Update line items in cart
 */
export async function updateCartLines(cartId, lines) {
  const query = `
    mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart {
          id
          createdAt
          updatedAt
          lines(first: 100) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    product {
                      id
                      title
                      handle
                      images(first: 1) {
                        edges {
                          node {
                            src
                            altText
                          }
                        }
                      }
                    }
                  }
                }
                attributes {
                  key
                  value
                }
              }
            }
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
            subtotalAmount {
              amount
              currencyCode
            }
          }
          checkoutUrl
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const data = await storefrontFetch(query, { cartId, lines });
  
  if (data.cartLinesUpdate.userErrors.length > 0) {
    throw new Error(data.cartLinesUpdate.userErrors.map(e => e.message).join(', '));
  }

  return data.cartLinesUpdate.cart;
}

/**
 * Transform cart data to match the old checkout format for compatibility
 */
export function transformCartToCheckoutFormat(cart) {
  if (!cart) return null;

  return {
    id: cart.id,
    webUrl: cart.checkoutUrl,
    completedAt: null, // Carts don't have a completedAt field
    lineItems: cart.lines.edges.map(({ node }) => ({
      id: node.id,
      title: node.merchandise.product.title,
      quantity: node.quantity,
      variant: {
        id: node.merchandise.id,
        title: node.merchandise.title,
        price: {
          amount: node.merchandise.price.amount,
          currencyCode: node.merchandise.price.currencyCode,
        },
        priceV2: node.merchandise.price,
        product: {
          // Extract the numeric ID from the GID for compatibility with existing code
          id: node.merchandise.product.id.replace('gid://shopify/Product/', ''),
          handle: node.merchandise.product.handle,
        },
        image: node.merchandise.product.images.edges[0]
          ? {
              src: node.merchandise.product.images.edges[0].node.src,
              altText: node.merchandise.product.images.edges[0].node.altText,
            }
          : null,
      },
      customAttributes: node.attributes,
    })),
    subtotalPrice: {
      amount: cart.cost.subtotalAmount.amount,
      currencyCode: cart.cost.subtotalAmount.currencyCode,
    },
    totalPrice: {
      amount: cart.cost.totalAmount.amount,
      currencyCode: cart.cost.totalAmount.currencyCode,
    },
    currencyCode: cart.cost.totalAmount.currencyCode,
  };
} 